<template>
  <b-card
    class="mb-0"
    no-body
    :header-bg-variant="hasError ? 'danger': null"
    :border-variant="hasError ? 'danger': null"
  >
    <b-card-header class="p-2">
      <b-row class="align-items-center">
        <b-col v-if="label" md="6" class="px-4 mb-1 mb-md-0">
          {{label}}

          <required-symbol v-if="required"/>
        </b-col>

        <b-col md="6" class="position-relative" ref="searchBox">
          <b-input
            v-model="searchPhrase"
            size="sm"
            :placeholder="$t('shared.search') + ' ...'"
            @blur.native="searchBlur"
            @focus.native="searchFocus"
          />

          <div v-if="searchResults.length" style="top:100%;left:0;right:0;z-index:1001;" class="position-absolute">
            <b-list-group class="px-3">
              <b-list-group-item
                class="p-1"
                dir="auto"
                button
                v-for="item in searchResults"
                :key="item.osm_id"
                @click.prevent="chooseSearchResult(item)"
              >
                {{item.label}}
              </b-list-group-item>
            </b-list-group>
          </div>
        </b-col>
      </b-row>
    </b-card-header>

    <b-card-body class="p-0 position-relative cntr">
      <l-map ref="map" :zoom.sync="zoom" :center="showingPoint" @click="click">
        <l-tile-layer
          url="https://{s}.tile.osm.org/{z}/{x}/{y}.png"
          attribution="<a href='https://persina.com' target='_blank'>persina</a>"
        />
        <l-marker :lat-lng="showingPoint"/>
      </l-map>

      <b-btn
        v-if="autoDetect"
        size="sm"
        variant="light"
        class="btn-coordinates box-shadow-sm p-2 rounded-right bg-white"
        @click="getCoordinates"
      >
        <font-awesome-icon
          fixed-width
          :icon="icon"
          :spin="progressing"
          class="align-middle"
          :class="{'mr-1': $dir.ltr, 'ml-1': $dir.rtl}"
        />
        {{ $t('shared.autoLocation') }}
      </b-btn>
    </b-card-body>
  </b-card>
</template>

<script>
import { L, LMap, LTileLayer, LMarker } from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
})

import { OpenStreetMapProvider } from 'leaflet-geosearch'
var searchProvider = new OpenStreetMapProvider()

export default {
  name: 'GeopointEditor',
  components: {
    LMap,
    LTileLayer,
    LMarker
  },
  props: {
    value: { type: Object },
    label: { type: String },
    required: { type: Boolean, default: false },
    hasError: { type: Boolean, default: false },
    format: { type: String, default: 'latlon' }, // latlon | latlng
    autoDetect: { type: Boolean, default: false }
  },
  data() {
    return {
      progressing: false,
      searchPhrase: null,
      searchResults: []
    }
  },
  computed: {
    icon () {
      if (this.progressing) return 'compass'
      else return 'map-marker-alt'
    },
    point: {
      get () {
        if (!this.value || !this.value.lat) return null
        let result
        switch (this.format) {
          case 'latlon':
            result = [this.value.lat, this.value.lon]
            break
          case 'latlng':
          default:
            result = [this.value.lat, this.value.lng]
        }
        return result
      },
      set (val) {
        this.emitPoint(val && val[0], val && val[1])
      }
    },
    zoom: {
      get () {
        return (this.value && this.value.zoom) || this.$config.map.defaultZoom
      },
      set (val) {
        if (this.value && this.value.zoom !== val)
          this.emitZoom(val)
      }
    },
    showingPoint () {
      return this.point || [this.$config.map.defaultLat, this.$config.map.defaultLng]
    }
  },
  watch: {
    searchPhrase (val) {
      if (val && val.length)
        this.search()
    }
  },
  methods: {
    emitPoint (lat, lon) {
      const result = { zoom: this.zoom, lat }

      switch (this.format) {
        case 'latlon':
          result.lon = lon
          break
        case 'latlng':
        default:
          result.lng = lon
      }

      this.emitInput(result)
    },
    emitZoom (zoom) {
      const result = { zoom, lat: this.value && this.value.lat }

      switch (this.format) {
        case 'latlon':
          result.lon = this.value.lon
          break
        case 'latlng':
        default:
          result.lng = this.value.lng
      }

      if (result.lat)
        this.emitInput(result)
    },
    emitInput (val) {
      if (!val || !val.lat)
        return this.$emit('input', null)

      this.$emit('input', val)
    },
    click (e) {
      this.point = [e.latlng.lat, e.latlng.lng]
    },
    getCoordinates() {
      this.progressing = true
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.progressing = false
          this.point = [
            pos.coords.latitude,
            pos.coords.longitude
          ]
        }
      )
    },
    search () {
      if (this.searchPhrase && this.searchPhrase.length) {
        searchProvider.search({ query: this.searchPhrase })
        .then(results => {
          this.searchResults = results
        })
      }
    },
    chooseSearchResult (item) {
      this.point = [item.raw.lat, item.raw.lon]
      this.searchResults = []
    },
    searchBlur (e) {
      if (!e.relatedTarget || !this.$refs.searchBox.contains(e.relatedTarget)) {
        this.searchResults = []
      }
    },
    searchFocus() {
      this.search()
    }
  }
}
</script>

<style scoped>
.cntr {
  height:300px;
  overflow: hidden;
}

.btn-coordinates {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 400;
  border: 2px solid rgba(0,0,0,0.2)
}
</style>

